// SDK entry (unminified)
// Auto-binds to all forms, fingerprints, sanitizes, and POSTs to ingestion endpoint

type Fields = Record<string, string>

interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

interface BrowserData {
  userAgent: string
  language: string
  platform: string
  screenWidth: number
  screenHeight: number
  viewportWidth: number
  viewportHeight: number
  timezone: string
  referrer: string
}

function sha1ish(input: string){
  let hash = 0
  for(let i=0;i<input.length;i++){ hash = (hash << 5) - hash + input.charCodeAt(i); hash |= 0 }
  return hash.toString(16)
}

function normalizedAction(form: HTMLFormElement){
  const raw = (form.getAttribute('action') || location.pathname).split('?')[0]
  try{
    const u = new URL(raw, location.origin)
    return u.pathname
  }catch{
    return raw
  }
}

function fingerprintForm(form: HTMLFormElement){
  const action = normalizedAction(form)
  const method = (form.getAttribute('method') || 'GET').toUpperCase()
  const custom = (form.getAttribute('data-formsync-name') || '').trim()
  const id = (form.getAttribute('id') || '').trim()
  const name = (form.getAttribute('name') || '').trim()
  const idx = (form as any).dataset?.formsyncIdx || ''
  const hint = custom || id || name || idx
  // Do NOT include field names so changes don’t fork worksheets
  return sha1ish(method + '|' + action + '|' + hint)
}

function sanitizeFields(data: Fields){
  const out: Fields = {}
  for(const [k,v] of Object.entries(data)){
    const lower = k.toLowerCase()
    if(/password|pwd|pass|card|cc|cvv|ssn/.test(lower)) continue
    const sv = String(v)
    out[k] = sv.length > 2000 ? sv.slice(0,2000) : sv
  }
  return out
}

function collectFields(form: HTMLFormElement): Fields{
  const fd = new FormData(form)
  const data: Fields = {}
  for(const [k, v] of fd.entries()){
    if(typeof v === 'string') data[k] = v
  }
  return data
}

function getUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined
  }
}

function getBrowserData(): BrowserData {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform || '',
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer
  }
}

(function(){
  const globalAny: any = window as any
  const state: any = globalAny.__formsync || (globalAny.__formsync = { q: [] })

  function init(config: { clientKey: string, clientSecret?: string }){
    const clientKey = config.clientKey
    state.k = clientKey
    if(!clientKey) return
    if(document.documentElement.getAttribute('data-formsync') === 'off') return

    const endpoint = state.endpoint || '/api/ingest'

    async function handleSubmit(ev: Event){
      try{
        const form = ev.target as HTMLFormElement
        const utmParams = getUTMParams()
        const browserData = getBrowserData()
        
        const payload = {
          clientKey,
          url: location.href,
          ua: navigator.userAgent,
          fingerprint: fingerprintForm(form),
          action: form.getAttribute('action') || null,
          method: (form.getAttribute('method') || 'GET').toUpperCase(),
          fields: sanitizeFields(collectFields(form)),
          utm: utmParams,
          browser: browserData,
          t: Date.now()
        }
        const body = JSON.stringify(payload)
        const blob = new Blob([body], { type: 'application/json' })
        const e2e = (window as any).__E2E_TEST_MODE === true
        const debug = e2e || !!state.debug
        if (e2e) {
          // E2E: use fetch so tests can capture payloads
          try { await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, credentials: 'omit' }) } catch {}
        } else {
          // Prefer beacon for reliability; fall back to fetch
          if (navigator.sendBeacon) {
            try { navigator.sendBeacon(endpoint, blob); } catch {}
          } else {
            try { await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, credentials: 'omit' }) } catch {}
          }
        }

        // Also emit a lightweight capture event for debugging/metadata tests
        // This is intercepted in tests and fulfilled without server support.
        const capture = {
          url: location.href,
          formAction: form.getAttribute('action') || location.pathname,
          formMethod: (form.getAttribute('method') || 'GET').toUpperCase(),
          userAgent: navigator.userAgent,
          fields: sanitizeFields(collectFields(form))
        }
        if (debug) {
          // Only emit capture in local/dev or when explicitly configured.
          const isLocalHost = (
            location.hostname === 'localhost' ||
            location.hostname === '127.0.0.1' ||
            location.hostname === '::1' ||
            location.hostname.endsWith('.local')
          )
          const captureEndpoint = state.captureEndpoint || '/api/capture'
          if (e2e || isLocalHost || state.captureEndpoint) {
            try {
              await fetch(captureEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(capture),
                credentials: 'omit'
              })
            } catch {}
          }
        }
      }catch{}
    }

    function bindAll(){
      const counters: Record<string, number> = {}
      document.querySelectorAll('form').forEach(f => {
        const form = f as HTMLFormElement & { __fsBound?: boolean }
        // Skip any form that contains a password input (e.g., login/sign-up)
        try {
          if (form.querySelector('input[type="password"]')) return
        } catch {}
        const action = normalizedAction(form)
        const method = (form.getAttribute('method') || 'GET').toUpperCase()
        const group = method + '|' + action
        if(!form.getAttribute('data-formsync-idx')){
          const n = (counters[group] = (counters[group] || 0) + 1)
          form.setAttribute('data-formsync-idx', String(n))
        }
        if(!form.__fsBound){ form.addEventListener('submit', handleSubmit, { capture: true }); form.__fsBound = true }
      })
    }

    bindAll()
    new MutationObserver(bindAll).observe(document.documentElement, { subtree: true, childList: true })
  }

  state.init = init

  // Expose SDK globally
  const sdk = {
    init: init,
    version: '1.0.0'
  }
  
  globalAny.__FormSync = sdk

  // Auto-init if key present via loader snippet dataset or queued
  const script = document.currentScript as HTMLScriptElement | null
  if(script && (script as any).dataset && (script as any).dataset.key){
    const k = (script as any).dataset.key
    init({ clientKey: k })
  }

  window.addEventListener('load', () => {
    const s: any = (window as any).__formsync
    if(s && s.k) init({ clientKey: s.k })
  })
})()
